import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LayoutGrid, Stethoscope, Briefcase, ArrowRight } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const copy = {
  en: {
    title: 'Interested in Dr.Vet?',
    text: 'Whether you are a veterinary clinic looking to improve workflows or an organisation exploring partnership opportunities, we would be happy to connect.',
    cards: [
      { Icon: LayoutGrid, label: 'Explore the Platform', sub: 'Discover Dr.Vet modules', href: '#agents', external: false, isAnchor: true },
      { Icon: Stethoscope, label: 'For Clinics: Book a Demo', sub: 'See it in your practice', href: 'mailto:i@dr.vet?subject=Dr.Vet%20Demo%20Request', external: true, isAnchor: false },
      { Icon: Briefcase, label: 'For Investors & Partners', sub: 'Visit Investor Brief', href: '/invest', external: false, isAnchor: false },
    ],
  },
  uk: {
    title: 'Зацікавилися Dr.Vet?',
    text: 'Незалежно від того, чи ви ветеринарна клініка, що прагне покращити робочі процеси, чи організація, що розглядає можливості партнерства, ми будемо раді поспілкуватися.',
    cards: [
      { Icon: LayoutGrid, label: 'Дослідити платформу', sub: 'Відкрийте модулі Dr.Vet', href: '#agents', external: false, isAnchor: true },
      { Icon: Stethoscope, label: 'Для клінік: замовити демо', sub: 'Подивіться у своїй практиці', href: 'mailto:i@dr.vet?subject=Dr.Vet%20Demo%20Request', external: true, isAnchor: false },
      { Icon: Briefcase, label: 'Для інвесторів і партнерів', sub: 'Переглянути інвестиційний бриф', href: '/invest', external: false, isAnchor: false },
    ],
  },
  ja: {
    title: 'Dr.Vetにご興味をお持ちですか？',
    text: 'ワークフローの改善を目指す獣医クリニックの方も、パートナーシップを検討されている組織の方も、ぜひお気軽にご連絡ください。',
    cards: [
      { Icon: LayoutGrid, label: 'プラットフォームを見る', sub: 'Dr.Vetモジュールを確認', href: '#agents', external: false, isAnchor: true },
      { Icon: Stethoscope, label: 'クリニック向け：デモ予約', sub: '実際の診療で確認', href: 'mailto:i@dr.vet?subject=Dr.Vet%20Demo%20Request', external: true, isAnchor: false },
      { Icon: Briefcase, label: '投資家・パートナー向け', sub: '投資概要を見る', href: '/invest', external: false, isAnchor: false },
    ],
  },
};

export default function FinalCTASection() {
  const { lang } = useLanguage();
  const c = copy[lang];
  const langSuffix = lang === 'en' ? '' : `?lang=${lang}`;

  return (
    <section id="contact" className="section-padding section-alt">
      <div className="container-max max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{c.title}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{c.text}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {c.cards.map((card, i) => {
            const Icon = card.Icon;
            const href = card.isAnchor || card.external ? card.href : `${card.href}${langSuffix}`;
            const onClick = () => trackEvent('home_final_cta_click', { card: card.label });

            const inner = (
              <>
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-primary" />
                </div>
                <h3 className="text-base font-bold text-foreground mb-1">{card.label}</h3>
                <p className="text-sm text-muted-foreground mb-4">{card.sub}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  <ArrowRight size={14} />
                </span>
              </>
            );

            const cls = 'card-elevated p-6 group hover:border-primary/60 border-2 border-transparent transition-all flex flex-col h-full';

            if (card.isAnchor || card.external) {
              return (
                <motion.a
                  key={i}
                  href={href}
                  onClick={onClick}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={cls}
                >
                  {inner}
                </motion.a>
              );
            }

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={href} onClick={onClick} className={cls}>
                  {inner}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
